// Toggle de thème — bouton dropdown avec 3 options : Clair / Sombre / Système
import React, { useState, useRef, useEffect } from 'react';
import { Sun, Moon, Monitor, Check } from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';

const OPTIONS = [
  { id: 'light',  label: 'Clair',   Icon: Sun },
  { id: 'dark',   label: 'Sombre',  Icon: Moon },
  { id: 'system', label: 'Système', Icon: Monitor }
];

const ThemeToggle = ({ compact = false }) => {
  const { theme, setTheme, resolvedTheme } = useTheme();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  // Fermeture au clic extérieur ou Escape
  useEffect(() => {
    if (!open) return;
    const handleClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    const handleKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', handleClick);
    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('mousedown', handleClick);
      document.removeEventListener('keydown', handleKey);
    };
  }, [open]);

  const CurrentIcon = resolvedTheme === 'dark' ? Moon : Sun;
  const label = theme === 'system' ? `Système (${resolvedTheme === 'dark' ? 'sombre' : 'clair'})` : (theme === 'dark' ? 'Sombre' : 'Clair');

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className={`flex items-center gap-2 rounded-lg transition-colors min-h-[40px] ${
          compact
            ? 'p-2 hover:bg-elevated-1'
            : 'px-3 py-2 hover:bg-elevated-1 text-text-secondary'
        }`}
        aria-label={`Changer le thème (actuellement : ${label})`}
        aria-haspopup="menu"
        aria-expanded={open}
        title={`Thème : ${label}`}
      >
        <CurrentIcon className="w-5 h-5" aria-hidden="true" />
        {!compact && <span className="text-sm font-medium hidden md:inline">{label}</span>}
      </button>

      {open && (
        <div
          role="menu"
          className="absolute right-0 top-full mt-1 z-50 w-44 bg-elevated-2 border border-border-subtle rounded-lg shadow-lg py-1"
        >
          {OPTIONS.map(({ id, label: optLabel, Icon }) => {
            const isActive = theme === id;
            return (
              <button
                key={id}
                role="menuitemradio"
                aria-checked={isActive}
                onClick={() => { setTheme(id); setOpen(false); }}
                className={`w-full flex items-center justify-between gap-2 px-3 py-2 text-sm text-left transition-colors ${
                  isActive ? 'text-text-primary font-medium' : 'text-text-secondary hover:text-text-primary'
                } hover:bg-elevated-1`}
              >
                <span className="flex items-center gap-2">
                  <Icon className="w-4 h-4" aria-hidden="true" />
                  {optLabel}
                </span>
                {isActive && <Check className="w-4 h-4 text-indigo-500" aria-hidden="true" />}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default ThemeToggle;
